<template>
  <div class="py-2 px-4">
    <h1 class="flex text-2xl">
      Dashboard
    </h1>

    <div class="py-2 flex space-x-4">
      <div class="flex flex-col">
        <div>Sales Between</div>
        <v-date-picker v-model="range" mode="range" @input="loadReportData()" color="green" />
      </div>

      <StoreFrontLinks />
    </div>

    <div class="grid grid-cols-2 gap-8">
      <div class="py-2 px-4 bg-white border border-gray-300 rounded-lg">
        <h2 class="flex justify-between text-xl">
          <span>Sales by Representative:</span>
          <span>{{ totalSalesByRepresentative | currency }}</span>
        </h2>
        <div class="grid grid-cols-4">
          <div class="colspan-2">Representative</div>
          <div class="text-right">Sales</div>
          <div class="text-right">Count</div>
        </div>
        <div v-for="sales in salesByRepresentative" :key="sales.representative" @click="toggleRow(sales.representative)">
          <div class="grid grid-cols-4">
            <div v-show="showRepresentativeDrilldown">
              <svg class="w-3 transition-all duration-200 transform mt-2" :class="{
                  '-rotate-90': !rowExpanded[sales.representative],
                  'rotate-0': rowExpanded[sales.representative],
                }" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 10"
                aria-hidden="true">
                <path d="M15 1.2l-7 7-7-7" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
            <div>{{ sales.representative }}</div>
            <div class="text-right">{{ sales.total | currency }}</div>
            <div class="text-right">{{ sales.count }}</div>
          </div>
          <div v-show="showRepresentativeDrilldown && rowExpanded[sales.representative]">
            <div class="grid grid-cols-2">
              <div>Order Number</div>
              <div>Total</div>
            </div>
            <div v-for="(order, index) in ordersByRepresentative[sales.representative]" :key="index"
              class="grid grid-cols-2">
              <div><a class="link" :href="orderLink(order.order_number)">{{
                order.order_number
              }}</a></div>
              <div>{{ order.total | currency }}</div>
            </div>
          </div>
        </div>
      </div>
      <div class="space-y-4">
        <div class="py-2 px-4 text-white bg-blue-600 border border-blue-700 rounded-lg">
          <h2 class="flex justify-between text-xl">
            <span>Total Sales:</span>
            <span>{{ totalSales | currency }}</span>
          </h2>
          <h2 class="flex justify-between text-xl">
            <span>Total Count:</span>
            <span>{{ totalCount }}</span>
          </h2>
        </div>
        <div class="py-2 px-4 text-white bg-blue-500 border border-blue-600 rounded-lg">
          <h2 class="text-xl">Sales by Channel</h2>
          <table class="w-full">
            <tr>
              <th>Channel</th>
              <th>Sales</th>
              <th>Count</th>
            </tr>
            <tr v-for="(sales, index) in salesByChannel" :key="index">
              <td>{{ sales.channel_name }}</td>
              <td class="text-right">{{ sales.total | currency }}</td>
              <td class="text-right">{{ sales.count }}</td>
            </tr>
          </table>
        </div>
        <div class="py-2 px-4 text-white bg-green-500 border border-green-600 rounded-lg">
          Total prints: {{ salesPrints }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { differenceInDays } from "date-fns";
import { ApiFactory } from "@/api/ApiFactory.js";
import moneyFormatter from "@/mixins/moneyFormatter";
import StoreFrontLinks from "./components/StoreFrontLinks";

const Reports = ApiFactory.get("reports");

export default {
  name: "Dashboard",
  filters: {
    currency: moneyFormatter.format,
  },
  components: {
    StoreFrontLinks
  },
  mixins: [moneyFormatter],
  data() {
    return {
      ordersByRepresentative: {},
      salesByRepresentative: [],
      salesByChannel: [],
      salesPrints: null,
      range: {
        start: new Date(),
        end: new Date(),
      },
      rowExpanded: {},
      showRepresentativeDrilldown: false,
    };
  },
  computed: {
    totalSalesByRepresentative() {
      return this.salesByRepresentative.reduce(
        (sum, current) => sum + Number(current.total),
        0
      );
    },
    totalSales() {
      return this.salesByChannel.reduce(
        (sum, current) => sum + Number(current.total),
        0
      );
    },
    totalCount() {
      return this.salesByChannel.reduce(
        (sum, current) => sum + Number(current.count),
        0
      );
    },
  },
  created() {
    this.loadReportData();
  },
  methods: {
    loadReportData() {
      this.loadSalesByRepresentative();
      this.loadSalesByChannel();
      this.loadSalesPrints();

      this.rowExpanded = {}; // reset row expansion
      if (differenceInDays(this.range.end, this.range.start) <= 30) {
        this.loadOrdersByRepresentative();
        this.showRepresentativeDrilldown = true;
      } else {
        this.showRepresentativeDrilldown = false;
      }
    },
    loadOrdersByRepresentative() {
      Reports.getOrdersByRepresentative({
        date_start: this.range.start.toISOString().substr(0, 10),
        date_end: this.range.end.toISOString().substr(0, 10),
      }).then((response) => {
        const orders = response.data.body.filter((item) => item.representative);
        orders.forEach((order) => {
          if (!this.ordersByRepresentative[order.representative]) {
            this.ordersByRepresentative[order.representative] = [];
          }
          this.ordersByRepresentative[order.representative].push(order);
        });
      });
    },
    loadSalesByRepresentative() {
      Reports.getSalesByRepresentative({
        date_start: this.range.start.toISOString().substr(0, 10),
        date_end: this.range.end.toISOString().substr(0, 10),
      }).then((response) => {
        this.salesByRepresentative = response.data.body
          .filter((item) => item.representative)
          .sort((a, b) => b.total - a.total);
      });
    },
    loadSalesByChannel() {
      Reports.getSalesByChannel({
        date_start: this.range.start.toISOString().substr(0, 10),
        date_end: this.range.end.toISOString().substr(0, 10),
      }).then((response) => {
        this.salesByChannel = response.data.body.sort(
          (a, b) => b.total - a.total
        );
      });
    },
    loadSalesPrints() {
      Reports.getSalesPrints({
        date_start: this.range.start.toISOString().substr(0, 10),
        date_end: this.range.end.toISOString().substr(0, 10),
      }).then((response) => {
        this.salesPrints = response.data.body.total;
      });
    },
    orderLink(orderNumber) {
      return `/customers/orders/${orderNumber}`;
    },
    toggleRow(representative) {
      this.$set(
        this.rowExpanded,
        representative,
        !this.rowExpanded[representative]
      );
    },
  },
};
</script>

<style scoped>
.link {
  @apply ml-0 text-green-600 transition duration-150 ease-in-out;
}

.link:hover {
  @apply text-green-500 underline;
}

li {
  @apply inline-flex;
}
</style>